// import node module libraries
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";

import { Trash, Edit, MoreVertical, Send, Plus, Grid, List } from "react-feather";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Card,
  Row,
  Col,
  Dropdown,
  Image,
  Badge,
  Table,
  ListGroup,
  ProgressBar,
  Button,
  Form,
  InputGroup,
} from "react-bootstrap";

// import custom components
import { FormSelect } from "components/elements/form-select/FormSelect";
import GlobalFilter from "components/elements/advance-table/GlobalFilter";
import Pagination from "components/elements/advance-table/Pagination";
import LevelIcon from "components/marketing/common/miscellaneous/LevelIcon";

// import profile layout wrapper
import ProfileLayout from "./ProfileLayout";
import { formatDate, truncateTextByCount } from "helper/utils";

import { GET_MY_COURSES } from "helper/graphql/queries";
import { PATH_DASHBOARD } from "routes/Paths";
import {
  DELETE_COURSE,
  UPDATE_COURSE_ACTIVATION,
} from "helper/graphql/mutations";
import { Notify } from "notiflix";
import AlertModal from "components/elements/modal-alert/AlertModal";
import moment from "moment";

// ----------------------------------------------------------------------

const MyCourses = () => {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const [filtering, setFiltering] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'table'

  const [myCourses, setCourses] = useState([]);

  useQuery(GET_MY_COURSES, {
    onCompleted: (data) => {
      const _courses = data?.allCourses?.edges?.map((item) => item.node);
      setCourses(
        _courses?.filter((course) => course.creator.guid === user?.guid)
      );
    },
  });

  // The forwardRef is important!!
  // Dropdown needs access to the DOM node in order to position the Menu
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <Link
      to=""
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn-icon btn btn-ghost btn-sm rounded-circle"
    >
      {children}
    </Link>
  ));

  const ActionMenu = ({ course }) => {
    const [showDelete, setShowDelete] = useState(false);
    const [showSubmit, setShowSubmit] = useState(false);

    const [onUpdateCourseActivation] = useMutation(UPDATE_COURSE_ACTIVATION);
    const [onDeleteCourse] = useMutation(DELETE_COURSE);

    const handleUpdate = (status) => {
      onUpdateCourseActivation({
        variables: {
          guid: course?.guid,
          isActive: course?.isActive,
          status: status,
          title: course?.title,
          description: course?.description,
          language: course?.language,
          dificultyLevel: course?.dificultyLevel || "",
          creatorId: course?.creator?.guid,
          courseStartDate: course?.courseStartDate,
          courseEndDate: course?.courseEndDate,
        },
        awaitRefetchQueries: true,
        refetchQueries: [GET_MY_COURSES],
        onCompleted: () => {
          Notify.success("Course status updated successfully!");
          setShowSubmit(false);
          window.location.reload();
        },
        onError: () => {
          Notify.failure("Failed to update course!");
        },
        refetchQueries: [{ query: GET_MY_COURSES }],
      });
    };

    const handleDelete = (guid) => {
      onDeleteCourse({
        variables: { guid: guid },
        awaitRefetchQueries: true,
        refetchQueries: [GET_MY_COURSES],
        onCompleted: () => {
          Notify.success("Course deleted successfully!");
          setShowDelete(false);
        },
        onError: () => {
          Notify.failure("Failed to delete course!");
        },
      });
    };

    return (
      <>
        <Dropdown>
          <Dropdown.Toggle as={CustomToggle}>
            <MoreVertical size="15px" className="text-secondary" />
          </Dropdown.Toggle>
          <Dropdown.Menu align="end">
            <Dropdown.Header>SETTINGS</Dropdown.Header>
            {course?.status?.toLowerCase() !== "active" && (
              <>
                <Dropdown.Item
                  eventKey="1"
                  onClick={() => {
                    navigate(
                      PATH_DASHBOARD.instructor.editDetails(course?.guid)
                    );
                  }}
                >
                  {" "}
                  <Edit size="15px" className="dropdown-item-icon" /> Edit
                  Details
                </Dropdown.Item>

                <Dropdown.Item
                  eventKey="4"
                  onClick={() => {
                    navigate(PATH_DASHBOARD.instructor.edit(course?.guid));
                  }}
                >
                  {" "}
                  <Edit size="15px" className="dropdown-item-icon" /> Edit
                  Curriculum
                </Dropdown.Item>
              </>
            )}

            {course?.status?.toLowerCase() !== "active" && (
            <Dropdown.Item eventKey="2" onClick={() => setShowDelete(true)}>
              {" "}
              <Trash size="15px" className="dropdown-item-icon" /> Remove
            </Dropdown.Item>
            )}
            {course?.status?.toLowerCase() === "submitted" ||
            course?.status?.toLowerCase() === "active" ? null : (
              <Dropdown.Item eventKey="3" onClick={() => setShowSubmit(true)}>
                {" "}
                <Send size="15px" className="dropdown-item-icon" /> Submit for
                Review
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>

        <AlertModal
          isOpen={showSubmit}
          onClose={() => setShowSubmit(false)}
          handleYes={() => {
            handleUpdate("Submitted");
          }}
          title="Course Submission"
          message="Are you sure you want to submit this course for review?"
        />

        <AlertModal
          isOpen={showDelete}
          onClose={() => setShowDelete(false)}
          handleYes={() => {
            handleDelete(course?.guid);
          }}
          title="Delete Course"
          message="Are you sure you want to delete this course? This action cannot be undone."
        />
      </>
    );
  };

  const sortby = [
    { value: "Date Created", label: "Date Created" },
    { value: "Newest", label: "Newest" },
    { value: "High Rated", label: "High Rated" },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "introImage",
        header: "Courses",
        cell: ({ row }) => {
          return (
            <div
              className="d-lg-flex align-items-center course-cell"
              onClick={() => {
                if (
                  row?.original?.status !== "Submitted" &&
                  row?.original?.status !== "Active"
                ) {
                  navigate(PATH_DASHBOARD.instructor.edit(row?.original?.guid));
                }
              }}
            >
              <div>
                <Link href="#">
                  <Image
                    src={row.original?.introImage[0]}
                    alt=""
                    className="rounded img-4by3-lg course-thumbnail"
                  />
                </Link>
              </div>
              <div className="ms-lg-3 mt-2 mt-lg-0">
                <h4 className="mb-1 h5">
                  <Link href="#" className="text-wrap course-title-link">
                    {truncateTextByCount(row?.original?.title, 8)}
                  </Link>
                </h4>
                <ListGroup as="ul" bsPrefix="list-inline" className="fs-6 mb-0">
                  <ListGroup.Item as="li" bsPrefix="list-inline-item">
                    <LevelIcon level={row.original.dificultyLevel} />
                    {row.original.dificultyLevel}
                  </ListGroup.Item>
                </ListGroup>
                {row?.original?.progress && (
                  <ProgressBar
                    variant="success"
                    className="mt-2"
                    now={row?.original?.progress}
                    style={{ height: "3px" }}
                  />
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "courseStartDate",
        header: "Start Date",
        cell: ({ getValue }) => {
          return formatDate(getValue());
        },
      },
      {
        accessorKey: "courseEndDate",
        header: "End Date",
        cell: ({ getValue }) => {
          return formatDate(getValue());
        },
      },

      {
        accessorKey: "learningMode",
        header: "Learning Mode",
        cell: ({ row }) => {
          const mode = (row?.original?.learningMode || "").toLowerCase();

          const label = !mode
            ? "Online"
            : mode.charAt(0).toUpperCase() + mode.slice(1);
          const variant =
            mode === "online"
              ? "info"
              : mode === "physical"
              ? "secondary"
              : "primary";
          return <Badge bg={variant}>{label}</Badge>;
        },
      },

      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ getValue }) => {
          return formatDate(getValue());
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          return (
            <Badge
              bg={`${
                getValue() === "Draft"
                  ? "info"
                  : getValue() === "Active"
                  ? "success"
                  : getValue() === "Rejected"
                  ? "danger"
                  : "warning"
              } `}
            >
              {getValue()}
            </Badge>
          );
        },
      },

      {
        accessorKey: "action",
        header: "",
        cell: ({ row }) => {
          return <ActionMenu course={row.original} />;
        },
      },
    ],
    []
  );

  const data = useMemo(() => myCourses, [myCourses]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter: filtering,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setFiltering,
    debugTable: false,
    initialState: {
      columnVisibility: {
        title: false,
        level: false,
        duration: false,
      },
    },
  });

  // Filter courses based on search
  const filteredCourses = myCourses.filter((course) => {
    if (!filtering) return true;
    const searchLower = filtering.toLowerCase();
    return (
      course.title?.toLowerCase().includes(searchLower) ||
      course.status?.toLowerCase().includes(searchLower) ||
      course.dificultyLevel?.toLowerCase().includes(searchLower)
    );
  });

  // Grid View Component
  const GridView = () => (
    <Row className="g-4">
      {filteredCourses.map((course) => (
        <Col lg={4} md={6} sm={12} key={course.guid}>
          <Card className="course-grid-card h-100">
            {/* Course Image */}
            <div className="course-image-wrapper">
              <Image
                src={course?.introImage?.[0]}
                alt={course?.title}
                className="course-grid-image"
                onClick={() => {
                  if (
                    course?.status !== "Submitted" &&
                    course?.status !== "Active"
                  ) {
                    navigate(PATH_DASHBOARD.instructor.edit(course?.guid));
                  }
                }}
              />
              {/* Status Badge */}
              <div className="status-badge-overlay">
                <Badge
                  bg={`${
                    course?.status === "Draft"
                      ? "info"
                      : course?.status === "Active"
                      ? "success"
                      : course?.status === "Rejected"
                      ? "danger"
                      : "warning"
                  } `}
                  className="status-badge-large"
                >
                  {course?.status}
                </Badge>
              </div>
              {/* Action Menu */}
              <div className="action-menu-overlay">
                <ActionMenu course={course} />
              </div>
            </div>

            {/* Card Body */}
            <Card.Body className="course-grid-body">
              <div className="d-flex align-items-start justify-content-between mb-2">
                <div className="flex-grow-1">
                  <h5 className="course-grid-title mb-2">
                    {course?.title}
                  </h5>
                </div>
              </div>

              {/* Meta Info */}
              <div className="course-meta-grid mb-3">
                <div className="meta-item">
                  <i className="fe fe-calendar text-muted me-1"></i>
                  <span className="meta-label">Start:</span>
                  <span className="meta-value">
                    {moment(course?.courseStartDate).format("MMM DD, YYYY")}
                  </span>
                </div>
                <div className="meta-item">
                  <i className="fe fe-calendar text-muted me-1"></i>
                  <span className="meta-label">End:</span>
                  <span className="meta-value">
                    {moment(course?.courseEndDate).format("MMM DD, YYYY")}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="course-grid-footer">
                <div className="d-flex align-items-center gap-2">
                  <Badge
                    bg=""
                    className={`mode-badge ${
                      course?.learningMode?.toLowerCase() === "online"
                        ? "mode-online"
                        : course?.learningMode?.toLowerCase() === "physical"
                        ? "mode-physical"
                        : "mode-hybrid"
                    }`}
                  >
                    <i className="fe fe-monitor me-1"></i>
                    {course?.learningMode || "Online"}
                  </Badge>
                  <Badge bg="" className="level-badge-grid">
                    <LevelIcon level={course?.dificultyLevel} />
                    <span className="ms-1">{course?.dificultyLevel}</span>
                  </Badge>
                </div>
                <div className="created-date">
                  <i className="fe fe-clock me-1"></i>
                  {moment(course?.createdAt).fromNow()}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  return (
    <ProfileLayout>
      <div className="my-courses-container">
        {/* Header Section */}
        <div className="page-header-section mb-4">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              {/* <h2 className="page-title mb-2">
                <i className="fe fe-book-open me-2"></i>
                My Courses
              </h2>
              <p className="page-subtitle">
                Manage your courses and track their status
              </p> */}
            </div>
            <Button
              variant="primary"
              className="create-course-btn"
              onClick={() => navigate(PATH_DASHBOARD.instructor.addNewCourse)}
            >
              <Plus size={18} className="me-2" />
              Create New Course
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {/* <Row className="g-3 mb-4">
          <Col lg={3} md={6} sm={12}>
            <Card className="stats-card">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                  <div className="stats-icon stats-icon-primary">
                    <i className="fe fe-book"></i>
                  </div>
                  <div className="ms-3">
                    <div className="stats-label">Total Courses</div>
                    <div className="stats-value">{myCourses.length}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <Card className="stats-card">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                  <div className="stats-icon stats-icon-success">
                    <i className="fe fe-check-circle"></i>
                  </div>
                  <div className="ms-3">
                    <div className="stats-label">Active</div>
                    <div className="stats-value">
                      {
                        myCourses.filter((c) => c.status === "Active").length
                      }
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <Card className="stats-card">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                  <div className="stats-icon stats-icon-warning">
                    <i className="fe fe-clock"></i>
                  </div>
                  <div className="ms-3">
                    <div className="stats-label">Pending</div>
                    <div className="stats-value">
                      {
                        myCourses.filter((c) => c.status === "Submitted")
                          .length
                      }
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <Card className="stats-card">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                  <div className="stats-icon stats-icon-info">
                    <i className="fe fe-edit"></i>
                  </div>
                  <div className="ms-3">
                    <div className="stats-label">Drafts</div>
                    <div className="stats-value">
                      {myCourses.filter((c) => c.status === "Draft").length}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row> */}

        {/* Filters & Controls */}
        <Card className="filters-card mb-4">
          <Card.Body>
            <Row className="align-items-center g-3">
              <Col lg={6} md={12}>
                <InputGroup className="search-input-group">
                  <InputGroup.Text className="search-icon-wrapper">
                    <i className="fe fe-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    placeholder="Search your courses..."
                    value={filtering}
                    onChange={(e) => setFiltering(e.target.value)}
                    className="search-input-modern"
                  />
                </InputGroup>
              </Col>
              <Col lg={3} md={6}>
                <FormSelect options={sortby} placeholder="Sort by" />
              </Col>
              <Col lg={3} md={6}>
                <div className="view-toggle-group">
                  <Button
                    variant={viewMode === "grid" ? "primary" : "outline-secondary"}
                    className="view-toggle-btn"
                    onClick={() => setViewMode("grid")}
                  >
                    <Grid size={18} />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "primary" : "outline-secondary"}
                    className="view-toggle-btn"
                    onClick={() => setViewMode("table")}
                  >
                    <List size={18} />
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Content Area */}
        {viewMode === "grid" ? (
          filteredCourses.length === 0 ? (
            <Card className="empty-state-card">
              <Card.Body className="text-center py-5">
                <i
                  className="fe fe-book-open"
                  style={{
                    fontSize: "4rem",
                    color: "#cbd5e1",
                    marginBottom: "1.5rem",
                  }}
                ></i>
                <h4 className="mb-2">No courses found</h4>
                <p className="text-muted mb-4">
                  {filtering
                    ? "Try adjusting your search terms"
                    : "Get started by creating your first course"}
                </p>
                {!filtering && (
                  <Button
                    variant="primary"
                    onClick={() =>
                      navigate(PATH_DASHBOARD.instructor.addNewCourse)
                    }
                  >
                    <Plus size={18} className="me-2" />
                    Create Your First Course
                  </Button>
                )}
              </Card.Body>
            </Card>
          ) : (
            <GridView />
          )
        ) : (
          <Card className="table-card">
            <Card.Body className="p-0">
              <Table hover responsive className="text-nowrap table-centered modern-table">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows?.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
              {/* Pagination @ Footer */}
              <div className="p-4 border-top">
                <Pagination table={table} />
              </div>
            </Card.Body>
          </Card>
        )}
      </div>

      <style jsx>{`
        /* Container */
        .my-courses-container {
          padding: 0;
        }

        /* Page Header */
        // .page-header-section {
        //   // background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        //   // padding: 2rem;
        //   border-radius: 16px;
        //   // border-left: 4px solid #F68521;
        // }

        .page-title {
          font-size: 1.875rem;
          font-weight: 800;
          color: #1e293b;
          display: flex;
          align-items: center;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #64748b;
          margin: 0;
        }

        .create-course-btn {
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          border-radius: 10px;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border: none;
          display: flex;
          align-items: center;
          white-space: nowrap;
        }

        .create-course-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(246, 133, 33, 0.3);
        }

        /* Stats Cards */
        .stats-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }

        .stats-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .stats-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          font-size: 1.5rem;
        }

        .stats-icon-primary {
          background: rgba(246, 133, 33, 0.1);
          color: #F68521;
        }

        .stats-icon-success {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .stats-icon-warning {
          background: rgba(251, 191, 36, 0.1);
          color: #fbbf24;
        }

        .stats-icon-info {
          background: rgba(29, 158, 176, 0.1);
          color: #1d9eb0;
        }

        .stats-label {
          font-size: 0.8125rem;
          color: #94a3b8;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stats-value {
          font-size: 1.75rem;
          font-weight: 800;
          color: #1e293b;
        }

        /* Filters Card */
        .filters-card {
          border: none;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .search-input-group {
          border-radius: 10px;
          overflow: hidden;
        }

        .search-icon-wrapper {
          background: white;
          border: 2px solid #e2e8f0;
          border-right: none;
          color: #94a3b8;
        }

        .search-input-modern {
          border: 2px solid #e2e8f0;
          border-left: none;
          padding: 0.75rem 1rem;
          font-size: 0.9375rem;
        }

        .search-input-modern:focus {
          border-color: #F68521;
          box-shadow: none;
        }

        .search-input-modern:focus + .search-icon-wrapper {
          border-color: #F68521;
        }

        /* View Toggle */
        .view-toggle-group {
          display: flex;
          gap: 0.5rem;
        }

        .view-toggle-btn {
          padding: 0.75rem 1rem;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
        }

        /* Grid View */
        .course-grid-card {
          border: none;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .course-grid-card:hover {
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
          transform: translateY(-4px);
        }

        .course-image-wrapper {
          position: relative;
          width: 100%;
          height: 230px;
          overflow: hidden;
          background: #f1f5f9;
        }

        .course-grid-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .course-grid-card:hover .course-grid-image {
          transform: scale(1.05);
        }

        .status-badge-overlay {
          position: absolute;
          top: 12px;
          left: 12px;
          z-index: 2;
        }

        .status-badge-large {
          padding: 6px 14px;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .action-menu-overlay {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 2;
          background: white;
          border-radius: 1000%;
        }

        .course-grid-body {
          padding: 1.25rem;
        }

        .course-grid-title {
          font-size: 1.0625rem;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.8em;
        }

        .course-meta-grid {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8125rem;
        }

        .meta-label {
          color: #94a3b8;
          font-weight: 500;
        }

        .meta-value {
          color: #475569;
          font-weight: 600;
        }

        .course-grid-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 0.75rem;
          border-top: 1px solid #f1f5f9;
        }

        .mode-badge {
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          display: flex;
          align-items: center;
        }

        .mode-online {
          background: rgba(29, 158, 176, 0.1) !important;
          color: #1d9eb0 !important;
        }

        .mode-physical {
          background: rgba(100, 116, 139, 0.1) !important;
          color: #64748b !important;
        }

        .mode-hybrid {
          background: rgba(246, 133, 33, 0.1) !important;
          color: #F68521 !important;
        }

        .level-badge-grid {
          background: rgba(16, 185, 129, 0.1) !important;
          color: #10b981 !important;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          display: flex;
          align-items: center;
        }

        .created-date {
          font-size: 0.75rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Table View */
        .table-card {
          margin-top:6px;
          margin-left:0.4px;
          border: none;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }

        .modern-table {
          margin-bottom: 0;
        }

        .modern-table thead {
          background: #f8fafc;
        }

        .modern-table thead th {
          font-weight: 700;
          color: #475569;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.5px;
          padding: 1rem 1.5rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .modern-table tbody tr {
          transition: all 0.2s ease;
        }

        .modern-table tbody tr:hover {
          background: #f8fafc;
        }

        .modern-table tbody td {
          padding: 1rem 1.5rem;
          vertical-align: middle;
        }

        .course-cell {
          cursor: pointer;
        }

        .course-thumbnail {
          transition: transform 0.3s ease;
        }

        .course-cell:hover .course-thumbnail {
          transform: scale(1.05);
        }

        .course-title-link {
          color: #1e293b;
          transition: color 0.3s ease;
        }

        .course-title-link:hover {
          color: #F68521;
        }

        /* Empty State */
        .empty-state-card {
          border: 2px dashed #e2e8f0;
          background: #f8fafc;
          border-radius: 16px;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .page-header-section {
            padding: 1.5rem;
          }

          .page-title {
            font-size: 1.5rem;
          }

          .create-course-btn {
            width: 100%;
            justify-content: center;
            margin-top: 1rem;
          }
        }

        @media (max-width: 768px) {
          .stats-value {
            font-size: 1.5rem;
          }

          .view-toggle-group {
            width: 100%;
          }
        }
      `}</style>
    </ProfileLayout>
  );
};

export default MyCourses;